import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ChevronLeft } from 'lucide-react'
import { adminApi } from '@/utils/api'

type PersonAssessment = {
  assessmentId: string
  userId: string
  attemptNumber: number
  status: string
  currentSection: number
  totalSections: number
  progressPercentage: number
  scores?: Record<string, number>
  riskLevels?: Record<string, string>
  startedAt?: string
  completedAt?: string | null
  sections?: Array<{
    sectionNumber: number
    sectionName: string
    category: string
    totalScore: number
    riskLevel: string
    questionsAnswered: number
  }>
}

type LearningModule = {
  moduleId: string
  moduleTitle: string
  category: string
  progressPercentage: number
  isCompleted: boolean
  completedVideos: number
  totalVideos: number
  startedAt?: string
  completedAt?: string | null
}

type Person = {
  userId: string
  firstName: string
  lastName: string
  email: string
  gender: string
  assessments: PersonAssessment[]
  learningProgress: LearningModule[]
}

type CoupleInfo = {
  id: string
  contractNumber: string
  status: string
  organizationName: string
  progressPercentage: number
  isPersonOneCompleted: boolean
  isPersonTwoCompleted: boolean
}

type CoupleAssessmentsData = {
  couple: CoupleInfo
  personOne: Person
  personTwo: Person
}

const riskColor = (level?: string) => {
  if (!level) return 'text-gray-500'
  const l = level.toLowerCase()
  if (l === 'high') return 'text-red-600 font-semibold'
  if (l === 'moderate') return 'text-[#FF6600] font-semibold'
  return 'text-[#15803d]'
}

const statusBadge = (status?: string) => {
  const s = (status || '').toLowerCase().replace(/[-\s]/g, '_')
  if (s === 'completed') return 'bg-[#d1f4e0] text-[#15803d]'
  if (s === 'in_progress') return 'bg-[#fef3c7] text-[#d97706]'
  return 'bg-[#ffedd5] text-[#ea580c]'
}

const formatDate = (d?: string | null) => {
  if (!d) return '-'
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export default function AdminCoupleDetail() {
  const { coupleId } = useParams<{ coupleId: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<CoupleAssessmentsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!coupleId) {
      setError('Missing couple ID')
      setLoading(false)
      return
    }
    const fetch = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await adminApi.coupleAssessments(coupleId)
        const payload = (res as { data?: CoupleAssessmentsData }).data || (res as unknown as CoupleAssessmentsData)
        setData(payload)
      } catch (err) {
        setError((err as Error)?.message || 'Failed to load couple assessments')
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [coupleId])

  if (loading) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] flex items-center justify-center'>
        <p className='text-[#4e4d4d]'>Loading couple details...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
        <button onClick={() => navigate('/admin/couples')} className='flex items-center gap-2 text-[14px] text-[#4e4d4d] hover:text-[#151515] mb-4'>
          <ChevronLeft className='h-4 w-4' /> Back to Couples
        </button>
        <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 text-center'>
          <p className='text-red-600'>{error}</p>
        </div>
      </div>
    )
  }

  const couple = data?.couple
  const personOne = data?.personOne
  const personTwo = data?.personTwo

  return (
    <div className='w-full min-h-screen bg-[#edeff3] p-4 md:p-6 lg:p-8'>
      <div className='bg-white rounded-[24px] p-4 sm:p-6 md:p-8 space-y-6'>
        {/* Header */}
        <div className='flex items-center gap-4'>
          <button
            onClick={() => navigate('/admin/couples')}
            className='w-[32px] h-[32px] bg-[#eaeaea] rounded-full flex items-center justify-center hover:bg-[#d9d9d9]'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <div>
            <h1 className='text-[24px] sm:text-[32px] font-bold text-[#151515]'>Couple Detail</h1>
            <p className='text-[13px] sm:text-[14px] text-[#4e4d4d]'>{couple?.contractNumber} &middot; {couple?.organizationName}</p>
          </div>
          <span className={`ml-auto px-3 py-1 rounded-full text-xs font-medium capitalize ${statusBadge(couple?.status)}`}>
            {couple?.status?.replace(/_/g, ' ') || 'Unknown'}
          </span>
        </div>

        {/* Couple Overview */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Overall Progress</p>
            <p className='text-[24px] font-bold text-[#151515]'>{couple?.progressPercentage ?? 0}%</p>
            <div className='w-full h-[4px] bg-[#4e4d4d] bg-opacity-25 rounded-full mt-2'>
              <div className='h-full bg-[#FF6600] rounded-full' style={{ width: `${couple?.progressPercentage ?? 0}%` }} />
            </div>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Person One</p>
            <p className='text-lg font-semibold text-[#151515]'>{personOne?.firstName} {personOne?.lastName}</p>
            <p className={`text-[14px] mt-1 ${couple?.isPersonOneCompleted ? 'text-[#15803d]' : 'text-[#FF6600]'}`}>
              {couple?.isPersonOneCompleted ? 'Completed' : 'In Progress'}
            </p>
          </div>
          <div className='bg-white rounded-[10px] border border-[#e8e8e8] p-4'>
            <p className='text-[13px] text-[#4e4d4d] mb-1'>Person Two</p>
            <p className='text-lg font-semibold text-[#151515]'>{personTwo?.firstName} {personTwo?.lastName}</p>
            <p className={`text-[14px] mt-1 ${couple?.isPersonTwoCompleted ? 'text-[#15803d]' : 'text-[#FF6600]'}`}>
              {couple?.isPersonTwoCompleted ? 'Completed' : 'In Progress'}
            </p>
          </div>
        </div>

        {/* Side-by-side Person Comparison */}
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
          {[
            { label: 'Person One', person: personOne },
            { label: 'Person Two', person: personTwo },
          ].map(({ label, person }) => (
            <div key={label} className='bg-[rgba(237,239,243,0.44)] rounded-2xl p-4 space-y-5'>
              <div className='flex items-center justify-between'>
                <h2 className='text-[18px] font-bold text-[#151515]'>{person?.firstName} {person?.lastName}</h2>
                <span className='text-[13px] text-[#4e4d4d]'>{person?.email}</span>
              </div>

              {/* Assessments */}
              <div>
                <h3 className='text-[14px] font-semibold text-[#475467] mb-3'>Assessments ({person?.assessments?.length || 0})</h3>
                {(!person?.assessments || person.assessments.length === 0) ? (
                  <p className='text-[14px] text-[#4e4d4d]'>No assessments yet</p>
                ) : (
                  <div className='space-y-3'>
                    {person.assessments.map((a) => (
                      <div key={a.assessmentId} className='bg-white rounded-[10px] border border-[#e8e8e8] p-4 space-y-3'>
                        <div className='flex items-center justify-between'>
                          <div>
                            <span className='text-[14px] font-semibold text-[#101828]'>Attempt #{a.attemptNumber}</span>
                            <span className={`ml-2 px-2 py-0.5 rounded text-xs capitalize ${statusBadge(a.status)}`}>
                              {a.status?.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <button
                            onClick={() => navigate(`/admin/assessments/${a.assessmentId}`)}
                            className='text-xs px-3 py-1 rounded-[8px] bg-[#151515] hover:bg-[#2a2a2a] text-white font-bold'
                          >
                            View Details
                          </button>
                        </div>
                        <div className='flex gap-4 text-xs text-[#4e4d4d]'>
                          <span>Progress: {a.progressPercentage}%</span>
                          <span>Sections: {a.currentSection}/{a.totalSections}</span>
                          <span>Started: {formatDate(a.startedAt)}</span>
                          {a.completedAt && <span>Completed: {formatDate(a.completedAt)}</span>}
                        </div>

                        {/* Section Scores */}
                        {a.scores && (
                          <div className='grid grid-cols-2 gap-2 text-xs'>
                            {Object.entries(a.scores).map(([key, value]) => (
                              <div key={key} className='flex justify-between bg-[rgba(237,239,243,0.5)] rounded-[8px] px-2 py-1'>
                                <span className='text-[#475467] capitalize'>{key.replace(/_/g, ' ')}</span>
                                <span className={riskColor(a.riskLevels?.[key])}>
                                  {value} ({a.riskLevels?.[key] || '-'})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Learning Progress */}
              <div>
                <h3 className='text-[14px] font-semibold text-[#475467] mb-3'>Learning Progress ({person?.learningProgress?.length || 0})</h3>
                {(!person?.learningProgress || person.learningProgress.length === 0) ? (
                  <p className='text-[14px] text-[#4e4d4d]'>No learning activity</p>
                ) : (
                  <div className='space-y-2'>
                    {person.learningProgress.map((m) => (
                      <div key={m.moduleId} className='flex items-center justify-between bg-white rounded-[10px] border border-[#e8e8e8] px-3 py-2'>
                        <div>
                          <p className='text-[14px] font-medium text-[#101828]'>{m.moduleTitle}</p>
                          <p className='text-xs text-[#4e4d4d]'>{m.completedVideos}/{m.totalVideos} videos</p>
                        </div>
                        <div className='text-right'>
                          <p className='text-[14px] font-semibold text-[#101828]'>{m.progressPercentage}%</p>
                          <p className={`text-xs ${m.isCompleted ? 'text-[#15803d]' : 'text-[#FF6600]'}`}>
                            {m.isCompleted ? 'Completed' : 'In Progress'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
